import React, { createContext, useContext, useState } from 'react';
import { Vehicle } from '../types';

interface SelectionItem {
  vehicle: Vehicle;
  quantity: number;
}

interface SelectionContextType {
  selectedItems: SelectionItem[];
  addToSelection: (vehicle: Vehicle, quantity: number) => void;
  updateSelection: (vehicleId: string, quantity: number) => void;
  removeFromSelection: (vehicleId: string) => void;
  clearSelection: () => void;
  getSelectionTotal: () => number;
}

const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

export function SelectionProvider({ children }: { children: React.ReactNode }) {
  const [selectedItems, setSelectedItems] = useState<SelectionItem[]>([]);

  const addToSelection = (vehicle: Vehicle, quantity: number) => {
    setSelectedItems(prev => {
      const existingIndex = prev.findIndex(item => item.vehicle.id === vehicle.id);
      
      if (existingIndex >= 0) {
        // Update existing item
        const newItems = [...prev];
        newItems[existingIndex] = { vehicle, quantity };
        return newItems;
      } else {
        // Add new item
        return [...prev, { vehicle, quantity }];
      }
    });
  };

  const updateSelection = (vehicleId: string, quantity: number) => {
    if (quantity === 0) {
      removeFromSelection(vehicleId);
    } else {
      setSelectedItems(prev => 
        prev.map(item =>
          item.vehicle.id === vehicleId
            ? { ...item, quantity }
            : item
        )
      );
    }
  };

  const removeFromSelection = (vehicleId: string) => {
    setSelectedItems(prev => 
      prev.filter(item => item.vehicle.id !== vehicleId)
    );
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const getSelectionTotal = () => {
    const subtotal = selectedItems.reduce((sum, item) => 
      sum + (item.vehicle.price * item.quantity), 0
    );
    const tax = subtotal * 0.21;
    return subtotal + tax;
  };

  return (
    <SelectionContext.Provider value={{
      selectedItems,
      addToSelection,
      updateSelection,
      removeFromSelection,
      clearSelection,
      getSelectionTotal
    }}>
      {children}
    </SelectionContext.Provider>
  );
}

export function useSelection() {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
}